import React from "react";
import {
    Search,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Check,
    ChevronDown,
} from "lucide-react";
import {
    LineChart,
    Line,
    ResponsiveContainer,
    YAxis,
} from "recharts";
import { Skeleton } from "components/ui/skeleton";
import type { FuelSidebarRow } from "../api";

interface Props {
    rows: FuelSidebarRow[];
    loading: boolean;
    error?: string;
    selectedCity: string;
    onCitySelect: (city: string) => void;
    search: string;
    onSearchChange: (value: string) => void;
    page: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    sortBy: "city" | "price";
    sortOrder: "asc" | "desc";
    onSortChange: (sortBy: "city" | "price", sortOrder: "asc" | "desc") => void;
}

const FuelSidebar: React.FC<Props> = ({
    rows,
    loading,
    error,
    selectedCity,
    onCitySelect,
    search,
    onSearchChange,
    page,
    pageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    sortBy,
    sortOrder,
    onSortChange,
}) => {

    const dropdownRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowSortMenu(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const [showSortMenu, setShowSortMenu] = React.useState(false);
    const sortOptions = [
        {
            label: "Cities: A-Z",
            sortBy: "city" as const,
            sortOrder: "asc" as const,
        },
        {
            label: "Cities: Z-A",
            sortBy: "city" as const,
            sortOrder: "desc" as const,
        },
        {
            label: "Prices: Low-High",
            sortBy: "price" as const,
            sortOrder: "asc" as const,
        },
        {
            label: "Prices: High-Low",
            sortBy: "price" as const,
            sortOrder: "desc" as const,
        },
    ];
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    const renderSparkline = (values?: number[], change?: number) => {
        if (!values?.length) {
            return null;
        }

        const data = values.map((v) => ({
            value: v,
        }));

        const stroke = (change || 0) > 0 ? "#dc2626" : "#16a34a";

        return (
            <div className="w-20 h-10">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                        data={data}
                        margin={{ top: 4, right: 0, left: 0, bottom: 4 }}
                    >
                        <YAxis
                            hide
                            domain={[
                                (dataMin: number) => dataMin - 0.3,
                                (dataMax: number) => dataMax + 0.3,
                            ]}
                        />

                        <Line
                            type="monotone"
                            dataKey="value"
                            stroke={stroke}
                            strokeWidth={2}
                            dot={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    return (
        <div className="h-full border rounded-xl bg-white flex flex-col overflow-hidden">
            <div className="p-4 border-b shrink-0">
                <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                        <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            size={16}
                        />

                        <input
                            value={search}
                            onChange={(e) =>
                                onSearchChange(e.target.value)
                            }
                            placeholder="Search city..."
                            className="w-full h-10 rounded-md border pl-9 pr-3 text-sm"
                        />
                    </div>

                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() =>
                                setShowSortMenu((p) => !p)
                            }
                            className="h-10 px-3 border rounded-md bg-white hover:bg-gray-50 flex items-center gap-2"
                        >
                            <span className="text-sm font-medium">
                                Sort
                            </span>

                            <ChevronDown size={16} />
                        </button>

                        {showSortMenu && (
                            <div className="absolute right-0 top-12 z-50 w-56 rounded-lg border bg-white shadow-lg py-1">
                                {sortOptions.map((option) => {
                                    const active =
                                        sortBy === option.sortBy &&
                                        sortOrder === option.sortOrder;

                                    return (
                                        <button
                                            key={option.label}
                                            onClick={() => {
                                                onSortChange(
                                                    option.sortBy,
                                                    option.sortOrder
                                                );

                                                setShowSortMenu(false);
                                            }}
                                            className="w-full px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between"
                                        >
                                            <span>
                                                {option.label}
                                            </span>

                                            {active && (
                                                <Check
                                                    size={16}
                                                    className="text-black"
                                                />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
                {loading ? (
                    <div className="p-4 space-y-4">
                        {Array.from({ length: 8 }).map(
                            (_, i) => (
                                <Skeleton key={i} className="h-16 w-full" />
                            )
                        )}
                    </div>
                ) : error ? (
                    <div className="p-6 text-red-600 text-sm">
                        {error}
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-6 text-sm text-gray-500">
                        No cities found
                    </div>
                ) : (
                    rows.map((row) => {
                        const isSelected = selectedCity === row.City;
                        const isPositive = row.Change > 0;

                        return (
                             <button
                                key={row.City}
                                onClick={() => onCitySelect(row.City)}
                                className={`w-full px-4 py-4 border-b transition hover:bg-gray-50
                                ${isSelected ? "bg-blue-50" : ""}
                                ${row.City.toLowerCase() === "index" ? "bg-gray-50" : ""}`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className="min-w-[110px] text-left">
                                        <div className="font-semibold text-sm">{row.City}</div>
                                    </div>

                                    <div className="flex-1 flex justify-center">
                                        {renderSparkline(row.Sparkline, row.Change)}
                                    </div>

                                    <div className="min-w-[90px] text-right">
                                        <div className="text-sm font-bold text-gray-900">
                                            ₹{row.CurrentRate.toFixed(2)}
                                        </div>

                                        <div className={`mt-1 flex items-center justify-end gap-2 text-xs font-semibold ${isPositive ? "text-red-600" : "text-green-600"}`}>
                                            <span>
                                                {isPositive ? "+" : ""}₹{row.Change.toFixed(2)}
                                            </span>

                                            <span>
                                                {isPositive ? "+" : ""} {row.ChangePercentage.toFixed(2)}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </button>
                        );
                    })
                )}
            </div>

            <div className="p-4 border-t bg-white shrink-0">
                <div className="flex flex-col gap-3">
                    <span className="text-sm text-gray-600">
                        Showing{" "}
                        <strong>
                            {totalItems === 0 ? 0 : (page - 1) * pageSize + 1}
                        </strong>{" "}
                        to{" "}
                        <strong>
                            {Math.min(page * pageSize, totalItems)}
                        </strong>{" "}
                        of <strong>{totalItems}</strong>
                    </span>

                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-sm">
                            <span className="whitespace-nowrap">
                                Rows 
                            </span>

                            <select
                                value={pageSize}
                                onChange={(e) =>
                                    onPageSizeChange(
                                        Number(e.target.value)
                                    )
                                }
                                className="border rounded px-2 py-1 text-sm"
                            >
                                {[10, 20, 50, 100].map((s) => (
                                    <option key={s} value={s}>
                                        {s}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                disabled={page === 1}
                                onClick={() => onPageChange(1)}
                                className="p-1 border rounded disabled:opacity-50"
                            >
                                <ChevronsLeft size={16} />
                            </button>

                            <button
                                disabled={page === 1}
                                onClick={() =>
                                    onPageChange(page - 1)
                                }
                                className="p-1 border rounded disabled:opacity-50"
                            >
                                <ChevronLeft size={16} />
                            </button>

                            <span className="px-2 text-sm whitespace-nowrap">
                                Page <strong>{page}</strong> / {totalPages}
                            </span>

                            <button
                                disabled={page === totalPages}
                                onClick={() =>
                                    onPageChange(page + 1)
                                }
                                className="p-1 border rounded disabled:opacity-50"
                            >
                                <ChevronRight size={16} />
                            </button>

                            <button
                                disabled={page === totalPages}
                                onClick={() =>
                                    onPageChange(totalPages)
                                }
                                className="p-1 border rounded disabled:opacity-50"
                            >
                                <ChevronsRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FuelSidebar;